import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Plus,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader,
  Info
} from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import { useTMSProjects } from '../../hooks/useTMSProjects'

const AddProjectModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation()

  // Use the TMS projects hook
  const {
    addProject,
    testConnection,
    isAddingProject,
    isTestingConnection,
    testConnectionError
  } = useTMSProjects()

  const [formData, setFormData] = useState({
    name: '',
    project_id: '',
    api_key: '',
    default_tags: [],
    default_overwrite_translations: true,
    default_flag_for_review: false,
    create_missing_locales: true
  })

  const [newTag, setNewTag] = useState('')
  const [errors, setErrors] = useState({})
  const [connectionStatus, setConnectionStatus] = useState(null) // null, 'testing', 'success', 'error'
  const [connectionCapabilities, setConnectionCapabilities] = useState(null)


  const handleClose = () => {
    setFormData({
      name: '',
      project_id: '',
      api_key: '',
      default_tags: [],
      default_overwrite_translations: true,
      default_flag_for_review: false,
      create_missing_locales: true
    })
    setNewTag('')
    setErrors({})
    setConnectionStatus(null)
    setConnectionCapabilities(null)
    onClose()
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear connection status when credentials change
    if (field === 'project_id' || field === 'api_key') {
      setConnectionStatus(null)
    }
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.default_tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        default_tags: [...prev.default_tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      default_tags: prev.default_tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleTestConnection = async () => {
    if (!formData.project_id || !formData.api_key) {
      setErrors({
        connection: t('tms.projectIdAndApiKeyRequired')
      })
      return
    }

    setConnectionStatus('testing')
    try {
      const data = await testConnection({
        projectId: formData.project_id,
        apiKey: formData.api_key
      })

      // Handle response based on success field, not just HTTP status
      if (data.success) {
        setConnectionStatus('success')
        setConnectionCapabilities(data)
        setErrors({})
        // Optionally update form with verified project name
        if (data.project_name && !formData.name) {
          setFormData(prev => ({ ...prev, name: data.project_name }))
        }
      } else {
        // Backend returned 200 but with success: false
        setConnectionStatus('error')
        setConnectionCapabilities(null)
        setErrors({
          connection: data.error_message || t('tms.connectionTestFailed')
        })
      }
    } catch (error) {
      setConnectionStatus('error')
      setConnectionCapabilities(null)
      setErrors({
        connection: error.response?.data?.detail || t('tms.connectionTestFailed')
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // Validation
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = t('validation.required')
    if (!formData.project_id.trim()) newErrors.project_id = t('validation.required')
    if (!formData.api_key.trim()) newErrors.api_key = t('validation.required')

    // Require successful connection test before allowing project creation
    if (connectionStatus !== 'success') {
      newErrors.connection = t('tms.connectionTestRequired')
      setErrors(newErrors)
      return
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    addProject(formData)
    .then((data) => {
      onSuccess?.(data)
      handleClose()
    })
    .catch((error) => {
      setErrors({
        submit: error.response?.data?.detail || t('tms.addProjectFailed')
      })
    })
  }

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'testing':
        return <Loader className="w-4 h-4 animate-spin text-blue-500" />
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('tms.addNewProject')}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="p-1"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Instructions */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-blue-700 dark:text-blue-300 font-medium mb-2">
                  {t('tms.howToGetCredentials')}
                </p>
                <ol className="text-blue-600 dark:text-blue-400 space-y-1 list-decimal list-inside">
                  <li>{t('tms.step1')}</li>
                  <li>{t('tms.step2')}</li>
                  <li>{t('tms.step3')}</li>
                  <li>{t('tms.step4')}</li>
                </ol>
                <a
                  href="https://support.phrase.com/hc/en-us/articles/5784094755484"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline mt-2"
                >
                  <ExternalLink className="w-3 h-3 mr-1" />
                  {t('tms.learnMore')}
                </a>
              </div>
            </div>
          </div>

          {/* General Error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Project Name */}
          <Input
            label={t('tms.projectName')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t('tms.projectNamePlaceholder')}
            error={errors.name}
            required
          />

          {/* Project ID */}
          <Input
            label={t('tms.projectId')}
            value={formData.project_id}
            onChange={(e) => handleInputChange('project_id', e.target.value)}
            placeholder={t('tms.projectIdPlaceholder')}
            error={errors.project_id}
            required
          />

          {/* API Key */}
          <Input
            label={t('tms.apiKey')}
            type="password"
            value={formData.api_key}
            onChange={(e) => handleInputChange('api_key', e.target.value)}
            placeholder={t('tms.apiKeyPlaceholder')}
            error={errors.api_key}
            required
          />

          {/* Connection Test */}
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={!formData.project_id || !formData.api_key || isTestingConnection}
              className="flex items-center"
            >
              {getConnectionStatusIcon()}
              <span className="ml-2">{t('tms.testConnection')}</span>
            </Button>

            {connectionStatus === 'success' && (
              <span className="text-sm text-green-600 dark:text-green-400">
                {t('tms.connectionSuccessful')}
              </span>
            )}
          </div>

          {errors.connection && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {errors.connection}
            </div>
          )}

          {/* Default Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tms.defaultTags')}
            </label>

            {/* Existing Tags */}
            {formData.default_tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.default_tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Tag Input */}
            <div className="flex space-x-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('tms.addTagPlaceholder')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Default Settings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('tms.defaultImportSettings')}
            </h4>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.default_overwrite_translations}
                  onChange={(e) => handleInputChange('default_overwrite_translations', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.overwriteExistingTranslations')}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.default_flag_for_review}
                  onChange={(e) => handleInputChange('default_flag_for_review', e.target.checked)}
                  disabled={connectionCapabilities && connectionCapabilities.can_verify_translations === false}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className={`ml-2 text-sm ${
                  connectionCapabilities && connectionCapabilities.can_verify_translations === false
                    ? 'text-gray-400 dark:text-gray-500'
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {t('tms.flagImportedStringsForReview')}
                  {connectionCapabilities && connectionCapabilities.can_verify_translations === false && (
                    <span className="ml-1 text-xs text-orange-600 dark:text-orange-400">
                      ({t('tms.reviewWorkflowNotAvailable')})
                    </span>
                  )}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.create_missing_locales}
                  onChange={(e) => handleInputChange('create_missing_locales', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.createMissingLocalesAutomatically')}
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isAddingProject}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isAddingProject}
              disabled={isAddingProject || isTestingConnection || connectionStatus !== 'success'}
            >
              {t('tms.addProject')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddProjectModal